import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Alert,
  TextField,
  Switch,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import LinkIcon from "@mui/icons-material/Link";

import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { formatPhoneNumber } from "../../utils/functions";
import AvatarUpload from "../shared/AvatarUpload";

const ChangePasswordSection = ({ api }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const handleSubmit = async () => {
    setAlert(null);
    if (newPassword.length < 8) {
      setAlert({ type: "error", message: "New password must be at least 8 characters." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setAlert({ type: "error", message: "Passwords do not match." });
      return;
    }
    setLoading(true);
    try {
      await api("/auth/change-password.php", {
        method: "POST",
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword }),
      });
      setAlert({ type: "success", message: "Password updated successfully." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      setAlert({ type: "error", message: e.message || "Failed to update password." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Change Password
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {alert && (
          <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ fontSize: 12, borderRadius: 1.5 }}>
            {alert.message}
          </Alert>
        )}
        <TextField
          type="password"
          size="small"
          placeholder="Current password"
          value={currentPassword}
          disabled={loading}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          placeholder="New password"
          value={newPassword}
          disabled={loading}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          placeholder="Confirm new password"
          value={confirmPassword}
          disabled={loading}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleSubmit}
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            sx={{
              backgroundColor: "#111008",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 1.5,
              px: 2,
              "&:hover": { backgroundColor: "#1e1c14" },
              "&.Mui-disabled": { backgroundColor: "rgba(0,0,0,0.15)", color: "rgba(0,0,0,0.4)" },
            }}
          >
            {loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Update Password"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Barbers receive booking alerts by push only (SMS/email always target the
// client). The push row reflects the shop's BARBER push toggle — the prefs
// endpoint scopes shop.push_notifications_enabled to this member's role — so a
// shop-disabled barber-push row renders locked with a note.
const NotificationsSection = ({ api }) => {
  const [prefs, setPrefs] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api("/tenants/notification-preferences.php", { method: "GET" });
      setPrefs(res.data.preferences);
      setShop(res.data.shop ?? null);
    } catch {
      setAlert({ type: "error", message: "Could not load notification settings." });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  const handleToggle = async (value) => {
    if (!prefs) return;
    const previous = prefs;
    const next = { ...prefs, push_notifications_enabled: value };
    setPrefs(next); // optimistic
    setSaving(true);
    setAlert(null);
    try {
      await api("/tenants/notification-preferences.php", {
        method: "PUT",
        body: JSON.stringify({
          email_notifications_enabled: next.email_notifications_enabled,
          sms_notifications_enabled: next.sms_notifications_enabled,
          push_notifications_enabled: next.push_notifications_enabled,
        }),
      });
    } catch (e) {
      setPrefs(previous); // revert on failure
      setAlert({ type: "error", message: e.message || "Failed to update notification settings." });
    } finally {
      setSaving(false);
    }
  };

  const shopOff = shop && shop.push_notifications_enabled === false;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Notifications
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {alert && (
          <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ fontSize: 12, borderRadius: 1.5 }}>
            {alert.message}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
            <CircularProgress size={16} sx={{ color: "text.secondary" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          prefs && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: shopOff ? "text.disabled" : "text.primary" }}
                >
                  Push notifications
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {shopOff
                    ? "Turned off by the shop."
                    : "New-booking and cancellation alerts on the mobile app."}
                </Typography>
              </Box>
              <Switch
                checked={!shopOff && !!prefs.push_notifications_enabled}
                disabled={saving || shopOff}
                onChange={(e) => handleToggle(e.target.checked)}
              />
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

const BarberProfile = () => {
  const { user, setUser } = useAuth();
  const api = useApi();
  const [copied, setCopied] = useState(false);

  const bookingLink = `${window.location.origin}/?barber=${user?.user_id}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(bookingLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <Box sx={{ p: { xs: 3, md: 5 }, maxWidth: 640 }}>
      {/* Header */}
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Profile
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Manage your account details and share your booking link.
      </Typography>

      {/* Avatar */}
      <AvatarUpload user={user} api={api} setUser={setUser} />

      {/* Account info */}
      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Account
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {[
          {
            label: "Name",
            value: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
          },
          { label: "Email", value: user?.email ?? "—" },
          {
            label: "Phone",
            value: user?.phone_number
              ? formatPhoneNumber(user.phone_number)
              : "—",
          },
          {
            label: "Role",
            value: user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "—",
          },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", minWidth: 80 }}
            >
              {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Change password */}
      <ChangePasswordSection api={api} />

      <Divider sx={{ mb: 4 }} />

      {/* Notifications (push only — see component note) */}
      <NotificationsSection api={api} />

      <Divider sx={{ mb: 4 }} />

      {/* Booking link */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="overline">Your Booking Link</Typography>
      </Box>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, fontSize: 12 }}>
        Share this link with clients to send them directly to your booking page — no barber selection needed.
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <LinkIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            color: "text.secondary",
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {bookingLink}
        </Typography>
        <Button
          size="small"
          startIcon={copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
          onClick={handleCopy}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            color: copied ? "success.main" : "#111008",
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: copied ? "success.main" : "#e0e0e0",
            px: 1.5,
            flexShrink: 0,
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          {copied ? "Copied!" : "Copy"}
        </Button>
      </Box>

      {copied && (
        <Alert severity="success" sx={{ mt: 2, fontSize: 12, borderRadius: 1.5 }}>
          Link copied to clipboard — ready to share with clients.
        </Alert>
      )}
    </Box>
  );
};

export default BarberProfile;
